import React, { useState, useEffect, MouseEvent, FC } from "react";
import Modal from "../Modal/Modal";
import MultipleInput from "../MultipleInput/MultipleInput";
import Button from "../Button/Button";
import { FCAdditionalProps } from "@datassential/platform-ui-lib";
import { components } from '@api/api';

export type KeywordItem = components['schemas']['MenuTrends.Application.Contracts.Models.FilterWord.FilterWordExtension'];

export type KeywordSettingsProps = Omit<FCAdditionalProps, "id"> & {
    shown: boolean,
    closeModal: (e?: MouseEvent) => void,
    x: number,
    y: number,
    keywords: KeywordItem[],
    includes: KeywordItem[],
    excludes: KeywordItem[],
    applySettings?: (id: number, data: KeywordItem[], name: string) => void,
    id: number,
    readOnly?: boolean,
    searchingKeyword: string,
};

const KeywordSettings: FC<KeywordSettingsProps> = (
    {
        shown,
        closeModal,
        x,
        y,
        keywords,
        includes = [],
        excludes = [],
        applySettings,
        id,
        readOnly = false,
        searchingKeyword,
    }
) => {
    const [isChanged, setIsChanged] = useState(false);
    const [localIncludes, setLocalIncludes] = useState<KeywordItem[]>(includes);
    const [localExcludes, setLocalExcludes] = useState<KeywordItem[]>(excludes);

    useEffect(() => {
        setLocalIncludes(includes);
        setLocalExcludes(excludes);
        setIsChanged(false);
    }, [shown, includes, excludes]);

    const handleSubmitKeyword = (type: string) => (keywords: KeywordItem[]) => {
        const sanitizedKeywords = keywords.map((keyword) => ({
            ...keyword,
            wordExtensions: [],
        }));
        switch (type) {
            case "includes": {
                setLocalIncludes([...sanitizedKeywords, ...localIncludes,]);
                break;
            }
            case "excludes": {
                setLocalExcludes([...sanitizedKeywords, ...localExcludes,]);
                break;
            }
            default:
                break;
        }
        setIsChanged(true);
    };

    const handleRemoveKeyword = (type: string) => (keywordName: string) => {
        switch (type) {
            case "includes": {
                setLocalIncludes(localIncludes.filter(i => (i ?? {}).name?.toLowerCase() !== keywordName.toLowerCase()));
                break;
            }
            case "excludes": {
                setLocalExcludes(localExcludes.filter(i => (i ?? {}).name?.toLowerCase() !== keywordName.toLowerCase()));
                break;
            }
            default:
                break;
        }
        setIsChanged(true);
    };

    return (
        <Modal hasOverlay closeModal={closeModal} shown={shown} closeOnOutsideClick={false}>
            <div
                style={{
                    left: x,
                    top: y,
                }}
                className="keyword-settings"
            >
                <div className="keyword-settings__top">
                    <div className="keyword-settings__top-title">
                        When searching for <span>{searchingKeyword ? searchingKeyword.toUpperCase() : ''}:</span>
                    </div>
                    <div className="keyword-settings__content">
                        <div className="keyword-settings__include">
                            <div className="keyword-settings__title">
                                also
                                <span className="keyword-settings__title--green">
                                     include
                                </span>
                            </div>
                            <div className="keyword-settings__input-wrap">
                                <MultipleInput
                                    readOnly={readOnly}
                                    submittedItems={localIncludes}
                                    onSubmit={handleSubmitKeyword('includes')}
                                    removeItem={handleRemoveKeyword('includes')}
                                    keywords={keywords}
                                    modifiers={['small']}
                                    placeholder="Type keyword"
                                    words={[
                                        ...localIncludes,
                                        ...localExcludes,
                                    ]}
                                    isUpperCase
                                />
                            </div>
                        </div>
                        <div className="keyword-settings__exclude">
                            <div className="keyword-settings__title">
                                but
                                <span className="keyword-settings__title--yellow">
                                     exclude
                                </span>
                            </div>
                            <div className="keyword-settings__input-wrap">
                                <MultipleInput
                                    readOnly={readOnly}
                                    submittedItems={localExcludes}
                                    onSubmit={handleSubmitKeyword('excludes')}
                                    removeItem={handleRemoveKeyword('excludes')}
                                    keywords={keywords}
                                    modifiers={['small']}
                                    placeholder="Type keyword"
                                    words={[
                                        ...localIncludes,
                                        ...localExcludes,
                                    ]}
                                    isUpperCase
                                />
                            </div>
                        </div>
                    </div>
                </div>
                {!readOnly && (
                    <div className="keyword-settings__bot">
                        <Button
                            onClick={closeModal}
                            className="keyword-settings__cancel"
                            modifiers={["small"]}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={() => {
                                applySettings?.(id, [
                                    ...localIncludes.map((word) => ({ ...word, wordType: 1 as const, })),
                                    ...localExcludes.map((word) => ({ ...word, wordType: 2 as const, })),
                                ], searchingKeyword);
                                closeModal();
                            }}
                            className="keyword-settings__apply"
                            isDisabled={!isChanged}
                            modifiers={["small"]}
                        >
                            Apply
                        </Button>
                    </div>
                )}

            </div>
        </Modal>
    );
};

export default KeywordSettings;
